"use client";

import { type JSX } from "react";
import { AppPlaceholder } from "@sitecore-content-sdk/nextjs";
import { extractHiddenBlock } from "../../../utils/styles";
import {
  SplitterParams,
  SplitterProps,
} from "components/sxa/column-splitter/column-splitter.props";
import componentMap from ".sitecore/component-map.client";

interface ColumnSplitterBaseConfig {
  variantName: "two" | "three" | "four" | "five";
  defaultEnabledPlaceholders: string;
}

const COLUMN_INDEXES = [1, 2, 3, 4, 5, 6, 7, 8] as const;

export const ColumnSplitterBase = (
  props: SplitterProps & { config: ColumnSplitterBaseConfig },
): JSX.Element => {
  const { page, params, rendering, config } = props;
  const { variantName, defaultEnabledPlaceholders } = config;

  const gridParams = params?.GridParameters ?? "basis-full lg:basis-full";
  const enableSpacing = params?.EnableSpacing === "1";
  const spacingClasses = enableSpacing
    ? "gap-y-8 md:gap-y-0 md:gap-x-8"
    : "gap-0";
  const tailwindClasses =
    `flex flex-wrap mx-auto ${spacingClasses} ${gridParams} ${
      params?.Styles ?? ""
    }`.trim();

  const columnWidths = COLUMN_INDEXES.map(
    (i) =>
      params?.[`ColumnWidth${i}` as keyof SplitterParams] as string | undefined,
  );
  const columnStyles = COLUMN_INDEXES.map(
    (i) => params?.[`Styles${i}` as keyof SplitterParams] as string | undefined,
  );

  const enabledParams =
    params?.EnabledPlaceholders ?? defaultEnabledPlaceholders;
  const enabledPlaceholders = enabledParams.split(",");
  const id = params?.RenderingIdentifier;
  const paddingPageBuilder = page.mode.isEditing ? "p-5" : "p-0";
  const hiddenBlockStyles = extractHiddenBlock(params?.GridParameters);

  return (
    <div
      className={`${variantName}-column-splitter-component ${tailwindClasses} ${paddingPageBuilder} ${hiddenBlockStyles}`}
      id={id || undefined}
      data-role={`${variantName}-column-splitter-component`}
      data-testid={`${variantName}-column-splitter`}
    >
      {enabledPlaceholders.map((ph, index) => {
        const phKey = `column-${ph}-{*}`;
        const defaultColumnWidth = "basis-full md:flex-1 min-w-0";
        const phStyles = `shrink-0 min-w-0 wrap-anywhere ${
          columnWidths[+ph - 1] || defaultColumnWidth
        } ${columnStyles[+ph - 1] || ""}`.trim();

        return (
          <div key={`col-${+ph}-${index}`} className={phStyles}>
            <div className="w-full">
              <AppPlaceholder
                name={phKey}
                rendering={rendering}
                page={page}
                componentMap={componentMap}
              />
            </div>
          </div>
        );
      })}
    </div>
  );
};
